/**
 * Verbatim owner-supplied homepage copy.
 *
 * These blocks are edited only by the owner. They MUST NOT be rewritten,
 * summarised, or punctuation-tweaked by any agent unless the owner has
 * explicitly asked for the change in the conversation. Wording is
 * intentionally split across paragraphs the way it reads aloud.
 *
 * Change log (owner-approved):
 *   - 2026-06 : Initial verbatim block from founder.
 *   - 2026-07-02: Owner requested clearer prose + bolder headlines on
 *     "The balance just shifted" and "Working in the background". New
 *     `heading` fields drive the serif H2 above each section eyebrow.
 *
 * Linter note: this file is added to SKIP_FILES in scripts/check_ai_tells.py
 * because the verbatim founder paragraph contains the phrase
 * "...even the building codes, and produced..." which trips the
 * Oxford-comma rule. The owner reviewed and approved that exact wording.
 */

export const BALANCE_SHIFTED = {
  label: "The balance just shifted",
  heading: "You do not have to fight this alone anymore.",
  paragraphs: [
    "The records. The templates. The years of doing this. The agent has always held the cards, and for most renters that has meant an unfair fight.",
    "TenantTrail evens it up. Every photo, every email, every date, kept on your side of the table. Locked. Time-stamped. Ready. The next time something is not right, you will not be digging through old inboxes hoping you saved the right message. You will have the receipts.",
    "Agents act differently when they know the renter is keeping records too.",
  ],
};

export const START_TODAY = {
  label: "Start today, not the day it goes wrong",
  paragraphs: [
    "Every month on TenantTrail, your position gets stronger. A few dated photos. A tidy record. A timeline building quietly in the background.",
    "The renters who win at move-out are the ones who started on day one. Not the day the dispute landed. By then it is too late to prove what the place looked like when you picked up the keys.",
    "Start your record now, while it costs you nothing.",
  ],
  ctaLabel: "Start free, no card needed",
  ctaHref: "/register",
};

export const BUILT_BY_RENTER = {
  label: "Built by a renter who won",
  paragraphs: [
    "TenantTrail wasn't built in a boardroom. It was built by a renter with a background in legal digital forensics who refused to back down. When their own tenancy went sideways, they put thousands of hours into tenancy law, the relevant acts, even the building codes, and produced hundreds of pages of dated, forensic proof. They won.",
    "Then came the obvious problem. No normal renter has thousands of hours or a forensics background. So that entire process got built into TenantTrail. Now you get the same firepower in a few minutes a month.",
  ],
  ctaLabel: "Read the founder's story",
  ctaHref: "/founder",
};

export const COMPASS_STRATEGIST = {
  label: "Compass · your strategist in the corner",
  heading: "Compass · your strategist in the corner",
  paragraphs: [
    "A rent rise that doesn't smell right. One inspection too many. A repair ignored for weeks. A bond they won't return. Whatever the agent throws at you, at any point in your tenancy, Compass knows the rule.",
    "It reads the letter, checks your lease against your state's tenancy laws, and tells you exactly how to respond. The acts, the codes, the awards. Quoted back in plain English, in a reply that's ready to send.",
  ],
  dialogue: {
    youLabel: "You",
    you: "My agent has booked another inspection, the third one this term. Can they even do that?",
    compassLabel: "Compass",
    compass: "Let me check your lease against your state's rules. This looks like more than they're allowed. Here's a reply that quotes the exact rule and your own agreement, ready to send.",
  },
  // CTA labels per the brief
  primaryCtaLabel: "See your reply",
  primaryCtaHref: "/ask",
  secondaryCtaLabel: "Ask Compass free",
  secondaryCtaHref: "/ask",
  // Reassurance line that must stay near the section (owner brief).
  reassurance: "Free, no sign-up needed. Compass tells you when it's worth a free call to your Tenants' Union.",
};
